import { Button, Input, Modal, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import ProjectSupportedBy from '../../components/cards/ProjectSupportedBy';

import { FullscreenSpin } from '../../components/FullscreenSpin';
import { CharacteristicsList } from '../../components/lists/CharacteristicsList';
import { OddImages } from '../../components/OddImages';
import { PlacesModal } from '../../components/PlacesModal';
import { theme } from '../../constants/theme';
import { Classification, Place } from '../../slices/classification';
import {
  FinancingNeed,
  reportFinancingNeedProblem,
} from 'src/slices/financingNeed';
import { TagListForDonorAndNgoPages } from 'src/components/TagListForNgoAndDonorPages';
import { useAppDispatch } from '../../hooks/store';

interface CharacteristicItem {
  title: React.ReactNode;
  description: React.ReactNode;
  valid: boolean | string | string[] | any;
}

const { Text } = Typography;

const { TextArea } = Input;

const useStyles = createUseStyles({
  subtitle: {
    color: theme.palette.primary,
    fontFamily: 'PP Mori',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '12.6px',
  },
  rightColumn: {
    flexGrow: 1,
    maxWidth: '230px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginLeft: '20px',
    gap: '20px',
  },
  problemButton: {
    fontFamily: 'PP Mori',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '12.6px',
    textDecoration: 'underline solid',
    color: '#797676 !important',
    '&:hover': {
      cursor: 'pointer',
      color: '#000 !important',
    },
  },
  tag: {
    '& .ant-tag': {
      margin: '0px 6px 0px 0px',
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      padding: 0,
      wordBreak: 'break-word',
      whiteSpace: 'unset',
      fontSize: '16px',
      lineHeight: '16.8px',
      fontWeight: '400',
      fontFamily: 'PP Mori',
    },
  },
  logoImageContainer: {
    height: '106px',
    width: '230px',
    objectFit: 'contain',
    overflow: 'hidden',
  },
  logoImage: {
    height: '100%',
    width: 'auto',
  },
});

interface Props {
  classification: Classification;
  project: FinancingNeed;
  ngoName: string;
}

const ProjectPageRightColumn: React.VFC<Props> = ({
  classification,
  project,
  ngoName,
}) => {
  const classes = useStyles();
  const [visible, setVisible] = React.useState(false);
  const [editedProblem, setEditedProblem] = React.useState<string>('');
  const dispatch = useAppDispatch();
  const [showPlacesModal, setShowPlacesModal] = useState(false);

  if (!classification || !project) return <FullscreenSpin />;

  const onEditProblem = (value: string) => {
    setEditedProblem(value);
  };

  const onSubmitProblem = () => {
    if (editedProblem) {
      dispatch(
        reportFinancingNeedProblem({
          _id: project._id,
          name: project.name,
          problemDescription: editedProblem,
        }),
      );
      setVisible(false);
    }
    setEditedProblem('');
  };

  const {
    sustainableDevelopmentGoals,
    targetPopulations,
    activityDomains,
    // activityZones,
    places,
  } = classification;

  const [countries, setCountries] = useState<Place[]>([]);
  const [france, setFrance] = useState<Place[]>([]);

  useEffect(() => {
    if (places) {
      setCountries(
        places
          .filter((place) => place.countries && place.countries.length)
          .sort((a, b) => a.name.localeCompare(b.name)) as Place[],
      );
      setFrance(
        places
          .filter((place) => place.departments && place.departments.length)
          .sort((a, b) =>
            a.name.includes('DROM') ||
            a.name.includes("Collectivités d'Outre-Mer")
              ? 1
              : a.name.localeCompare(b.name),
          ) as Place[],
      );
    }
  }, [places]);

  const activityDomainsFromTags = useMemo(() => {
    if (!project?.tags?.length || !classification.tags?.length) {
      return [];
    }

    return project.tags
      .reduce((previousValue: string[], tagId: string) => {
        const activityDomains = classification.tags.find(
          (t) => t._id === tagId,
        )?.associatedDomains;
        if (activityDomains) {
          return previousValue.concat(activityDomains);
        }
        return previousValue;
      }, [])
      .filter(
        (value: string, index: number, self: string[]) =>
          self.indexOf(value) === index,
      );
  }, [project.tags, classification.tags]);

  function renderFrance(depIds: string[]): string | undefined {
    if (!depIds || depIds.length === 0) return undefined;
    let isFranceComplete = true;
    france.forEach((place) => {
      if (place.departments) {
        place.departments.forEach((department) => {
          if (!depIds.includes(department._id)) {
            isFranceComplete = false;
          }
        });
      }
    });
    if (isFranceComplete) {
      return 'France';
    }
    return undefined;
  }

  function renderDepartments(depIds: string[]): string[] {
    if (!depIds || depIds.length === 0) return [];
    const results: string[] = [];
    france.forEach((place) => {
      let isRegionComplete = true;
      if (place.departments) {
        const departments = [] as string[];
        place.departments.forEach((department) => {
          if (!depIds.includes(department._id)) {
            isRegionComplete = false;
          } else {
            departments.push(department.name);
          }
        });
        if (!isRegionComplete) {
          departments.forEach((department) => {
            results.push(department);
          });
        }
      }
      if (isRegionComplete) {
        results.push(place.name);
      }
    });
    return results.length > 0 ? results : [];
  }

  function renderCountries(placesId: string[]) {
    if (!placesId || placesId.length === 0) return [];
    const results: string[] = [];
    countries.forEach((place) => {
      let isContinentComplete = true;
      if (place.countries) {
        const countries = [] as string[];
        place.countries.forEach((currCountry) => {
          if (!placesId.includes(currCountry._id)) {
            isContinentComplete = false;
          } else {
            countries.push(currCountry.name);
          }
        });
        if (!isContinentComplete) {
          countries.forEach((currentCountry) => {
            results.push(currentCountry);
          });
        }
      }
      if (isContinentComplete) {
        if (place.name === 'Europe') {
          results.push('Europe');
        } else {
          results.push(place.name);
        }
      }
    });
    return results.length > 0 ? results : [];
  }

  function renderPlaces(placeIds: string[]): JSX.Element[] {
    if (!placeIds || placeIds.length === 0) return [];
    let results = [] as string[];
    if (renderFrance(placeIds)) {
      const isFrance = renderFrance(placeIds);
      if (isFrance) {
        results = results.concat([isFrance]);
      }
    } else {
      if (renderDepartments(placeIds)) {
        results = results.concat(renderDepartments(placeIds));
      }
    }
    if (renderCountries(placeIds)) {
      results = results.concat(renderCountries(placeIds));
    }
    return results.length > 0
      ? results.map((department, index) =>
          index !== results.length - 1 ? (
            <Tag key={index} className={classes.tag}>
              {department}
              {', '}
            </Tag>
          ) : (
            <Tag key={index} className={classes.tag}>
              {department}
            </Tag>
          ),
        )
      : [];
  }

  const projectCharacteristics = [
    {
      title: (
        <Text className={classes.subtitle}>{"Lieu(x) d'intervention"}</Text>
      ),
      description: (
        <>
          {renderPlaces(project.places).slice(0, 10)}
          {renderPlaces(project.places).length > 10 && (
            <Button
              size={'small'}
              type="link"
              onClick={() => setShowPlacesModal(true)}
            >
              Voir Tout
            </Button>
          )}
        </>
      ),
      valid: project.places || [],
    },
    {
      title: (
        <Text className={classes.subtitle}>{"Lieu(x) d'intervention"}</Text>
      ),
      description: (
        <TagListForDonorAndNgoPages
          elementIds={
            project.roadAndNumber && project.city && project.zipcode
              ? [project.roadAndNumber]
              : []
          }
          collection={
            project.roadAndNumber && project.city && project.zipcode
              ? [
                  {
                    _id: project.roadAndNumber,
                    name: `${project.roadAndNumber}, ${project.city}, ${project.zipcode}`,
                  },
                ]
              : []
          }
          color="default"
          collectionName="uniqueCustomAddress"
        />
      ),
      valid: project.roadAndNumber && project.city && project.zipcode,
    },
    {
      title: (
        <Text className={classes.subtitle}>{"Lieu(x) d'intervention"}</Text>
      ),
      description: (
        <TagListForDonorAndNgoPages
          elementIds={project.uniqueZipcode ? [project.uniqueZipcode] : []}
          collection={
            project.uniqueZipcode
              ? [
                  {
                    _id: project.uniqueZipcode,
                    name: `${project.uniqueZipcode}, ${project.city}`,
                  },
                ]
              : []
          }
          color="default"
          collectionName="uniqueZipcode"
        />
      ),
      valid: project.uniqueZipcode ? [project.uniqueZipcode] : [],
    },
    {
      title: <Text className={classes.subtitle}>Bénéficiaires cibles</Text>,
      description: (
        <TagListForDonorAndNgoPages
          elementIds={project.targetPopulations || []}
          collection={targetPopulations}
          color="default"
          collectionName="targetPopulations"
        />
      ),
      valid: project.targetPopulations || [],
    },
  ].filter((item: CharacteristicItem) => {
    if (typeof item.valid === 'boolean') {
      return item.valid;
    }
    if (Array.isArray(item.valid)) {
      return item.valid.length > 0;
    }
    if (typeof item.valid === 'string') {
      return (
        item.valid.length > 0 &&
        item.valid !== 'undefined' &&
        item.valid !== 'null' &&
        item.valid !== '0' &&
        item.valid !== 'false'
      );
    }
    if (typeof item.valid === 'number') {
      return item.valid !== 0 && true;
    }
    return false;
  });

  const projectCharacteristics2 = [
    {
      title: (
        <Text className={classes.subtitle}>Domaines d&apos;intervention</Text>
      ),
      description: (
        <TagListForDonorAndNgoPages
          elementIds={activityDomainsFromTags}
          collection={activityDomains}
          color="default"
          collectionName="activityDomains"
        />
      ),
      valid: activityDomainsFromTags,
    },
  ].filter((item: CharacteristicItem) => {
    if (typeof item.valid === 'boolean') {
      return item.valid;
    }
    if (Array.isArray(item.valid)) {
      return item.valid.length > 0;
    }
    if (typeof item.valid === 'string') {
      return (
        item.valid.length > 0 &&
        item.valid !== 'undefined' &&
        item.valid !== 'null' &&
        item.valid !== '0' &&
        item.valid !== 'false'
      );
    }
    if (typeof item.valid === 'number') {
      return item.valid !== 0 && true;
    }
    return false;
  });

  return (
    <div className={classes.rightColumn}>
      <Text className={classes.problemButton} onClick={() => setVisible(true)}>
        Signaler une anomalie avec cette fiche
      </Text>
      {project.logoUrl && (
        <div className={classes.logoImageContainer}>
          <img
            src={project.logoUrl}
            className={classes.logoImage}
            alt={project.name}
          />
        </div>
      )}
      {projectCharacteristics.length > 0 && (
        <CharacteristicsList characteristics={projectCharacteristics} />
      )}
      {project.adminNgos[0] && <ProjectSupportedBy ngoName={ngoName} />}
      {projectCharacteristics2.length > 0 && (
        <CharacteristicsList characteristics={projectCharacteristics2} />
      )}
      <OddImages
        sustainableDevelopmentGoals={sustainableDevelopmentGoals}
        oddIds={project?.sustainableDevelopmentGoals || []}
      />
      <PlacesModal
        ngoPlaces={project.places}
        places={places}
        isVisible={showPlacesModal}
        requestClose={() => setShowPlacesModal(false)}
      />
      <Modal
        open={visible}
        title="Signalement d'une fiche projet"
        onOk={() => setVisible(false)}
        onCancel={() => setVisible(false)}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setVisible(false);
              setEditedProblem('');
            }}
          >
            Annuler
          </Button>,
          <Button key="submit" type="primary" onClick={() => onSubmitProblem()}>
            Valider
          </Button>,
        ]}
      >
        <TextArea
          placeholder="Merci de préciser le sujet avec la fiche de ce projet"
          rows={4}
          onChange={(e) => onEditProblem(e.target.value)}
          value={editedProblem}
        />
      </Modal>
    </div>
  );
};

export default ProjectPageRightColumn;
